import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  activateRevenueRule,
  ApiError,
  fetchRevenueRuleVersions,
  fetchRevenueRules,
  previewRevenueRule,
  revertRevenueRule,
} from '@/api/client'
import type { RevenueRulePreview, RevenueRuleVersionSummary } from '@/api/types'
import { formatDate } from '@/lib/format-date'

import { Receipt } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'
function todayIsoDate(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function RevenueRulesAdminPage() {
  const qc = useQueryClient()
  const versionsQuery = useQuery({
    queryKey: ['config', 'revenue-rules', 'versions'],
    queryFn: fetchRevenueRuleVersions,
  })
  const activeQuery = useQuery({
    queryKey: ['config', 'revenue-rules', 'active'],
    queryFn: fetchRevenueRules,
  })

  const [payloadText, setPayloadText] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState(todayIsoDate())
  const [closedReason, setClosedReason] = useState('')
  const [preview, setPreview] = useState<RevenueRulePreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const payload = activeQuery.data?.payload
    if (payload && !payloadText) {
      setPayloadText(JSON.stringify(payload, null, 2))
    }
  }, [activeQuery.data, payloadText])

  const activeVersion = activeQuery.data?.version ?? 0

  const versions = useMemo(() => versionsQuery.data ?? [], [versionsQuery.data])

  const previewMut = useMutation({
    mutationFn: async () => {
      const payload = JSON.parse(payloadText) as Record<string, unknown>
      return previewRevenueRule({ payload, effective_from: effectiveFrom })
    },
    onSuccess: (data) => {
      setError(null)
      setPreview(data)
    },
    onError: (err: unknown) => {
      setPreview(null)
      setError(err instanceof ApiError ? err.message : 'Preview failed')
    },
  })

  const activateMut = useMutation({
    mutationFn: async () => {
      const payload = JSON.parse(payloadText) as Record<string, unknown>
      return activateRevenueRule({
        payload,
        effective_from: effectiveFrom,
        closed_month_reason: closedReason.trim() || undefined,
      })
    },
    onSuccess: async () => {
      setError(null)
      setPreview(null)
      setClosedReason('')
      setPayloadText('')
      await qc.invalidateQueries({ queryKey: ['config', 'revenue-rules'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Activation failed')
    },
  })

  const revertMut = useMutation({
    mutationFn: (version: number) =>
      revertRevenueRule(version, {
        effective_from: todayIsoDate(),
        closed_month_reason: closedReason.trim() || undefined,
      }),
    onSuccess: async () => {
      setError(null)
      setPayloadText('')
      await qc.invalidateQueries({ queryKey: ['config', 'revenue-rules'] })
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Revert failed')
    },
  })

  const closedTouched = preview?.closed_months_touched ?? []
  const needsReason = closedTouched.length > 0

  return (
    <AdminSection
      title="Revenue rules"
      tone="amber"
      icon={Receipt}
      lede="Version billing types, proration, and worked examples. Preview portfolio impact before activating a new rule version (active: v{activeVersion})."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      <div className="admin-rule-list">
        <h3 className="admin-form__title">Versions</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Effective from</th>
              <th>Summary</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {versions.map((row: RevenueRuleVersionSummary) => (
              <tr key={row.id}>
                <td>{row.version}</td>
                <td>{formatDate(row.effective_from)}</td>
                <td>{row.summary}</td>
                <td>
                  {row.version !== activeVersion && (
                    <button
                      type="button"
                      className="button button--secondary button--small"
                      disabled={revertMut.isPending}
                      onClick={() => revertMut.mutate(row.version)}
                    >
                      Revert to this
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {versions.length === 0 && !versionsQuery.isLoading && (
              <tr>
                <td colSpan={4} className="empty">
                  No versions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <form
        className="admin-form"
        onSubmit={(event) => {
          event.preventDefault()
          previewMut.mutate()
        }}
      >
        <h3 className="admin-form__title">Proposed payload</h3>
        <div className="admin-form__row">
          <label className="field field--grow">
            <span className="field__label">Rule JSON</span>
            <textarea
              className="field__input field__input--code"
              rows={16}
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              spellCheck={false}
              required
            />
          </label>
        </div>
        <div className="admin-form__row">
          <label className="field">
            <span className="field__label">Effective from</span>
            <input
              className="field__input"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              required
            />
          </label>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => {
              const p = activeQuery.data?.payload
              if (p) {
                setPayloadText(JSON.stringify(p, null, 2))
                setPreview(null)
              }
            }}
          >
            Reset from active
          </button>
          <button
            className="button button--primary"
            type="submit"
            disabled={previewMut.isPending || !payloadText.trim()}
          >
            Preview impact
          </button>
        </div>
      </form>

      {preview && (
        <div className="admin-form">
          <h3 className="admin-form__title">Preview results</h3>
          <ul className="admin-example-list">
            {preview.worked_examples.map((ex) => (
              <li key={ex.name} className={ex.ok ? 'ok' : 'error'}>
                <strong>{ex.name}</strong>: {ex.detail} {ex.ok ? '✓' : '✗'}
              </li>
            ))}
          </ul>
          <p>
            Portfolio totals (open forecast years): before {preview.impact.before_total}, after{' '}
            {preview.impact.after_total}
          </p>
          {preview.impact.month_deltas.length > 0 && (
            <ul>
              {preview.impact.month_deltas.map((d) => (
                <li key={`${d.year}-${d.month}`}>
                  {d.year}-{String(d.month).padStart(2, '0')}: Δ {d.delta}
                </li>
              ))}
            </ul>
          )}
          {needsReason && (
            <p className="alert alert--warning" role="status">
              Closed months affected:{' '}
              {closedTouched.map((m) => `${m.year}-${String(m.month).padStart(2, '0')}`).join(', ')}
              . Activation requires a reason.
            </p>
          )}
          {needsReason && (
            <label className="field field--grow">
              <span className="field__label">Closed month reason</span>
              <input
                className="field__input"
                value={closedReason}
                onChange={(e) => setClosedReason(e.target.value)}
                placeholder="Why change rules affecting closed months?"
              />
            </label>
          )}
          <button
            type="button"
            className="button button--primary"
            disabled={
              activateMut.isPending ||
              preview.worked_examples.some((ex) => !ex.ok) ||
              (needsReason && !closedReason.trim())
            }
            onClick={() => activateMut.mutate()}
          >
            Activate new version
          </button>
        </div>
      )}
    </AdminSection>
  )
}
